import React, { useState, useMemo, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { FormField, StatusPicker, NumberField, ChoiceKeys, Combobox, Switch, Button, StatusPill, MoveHeaderCard } from '../ui';
import MoveDetailsEditor from './MoveDetailsEditor';
import { DEFAULT_ESTIMATE_STATUSES as ESTIMATE_STATUSES, JOB_TYPES, PACKING_OPTIONS, PACKING_LABELS, getPriorityOptions, getFurnitureClassOptions, estimateEffectiveRate, estimateExtraTruckSurcharge, estimateTruckLabel } from '../../lib/constants';
import { useEstimateStatusChange } from '../../hooks/useEstimateStatusChange';
import { useStore, useNav } from '../../store/StoreContext';
// Moves (2026-09-18, moves-architecture.md §3): dates / addresses / job type
// are edited on the MOVE and read anywhere. This editor's date block is the
// MoveHeaderCard (trip strip + route), and the address editor saves to the
// move; saveEstimate keeps handling pricing / notes / crew fields.
import { moveForEstimate, optionsForMove, legsForMove, fmtMoveDate, optionLabel, moveFieldsFromClientShape, MOVE_IDENTITY_FIELDS } from '../../lib/moves';
import { useAuth } from '../../auth/AuthContext';
import PriorityPicker from '../ui/PriorityPicker';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import ClientNotesPanel from './ClientNotesPanel';
import db from '../../lib/database';
import { phasePackPricing, phaseHasPackPricing } from '../../lib/phaseKinds';

// Friendly "Jun 16, 2026" from a YYYY-MM-DD string (noon-anchored to dodge TZ).
function fmtDate(d) {
    if (!d) return null;
    try { return format(new Date(d + 'T12:00:00'), 'MMM d, yyyy'); } catch { return d; }
}
function dayDelta(from, to) {
    if (!from || !to) return 0;
    return Math.round((new Date(to + 'T12:00:00') - new Date(from + 'T12:00:00')) / 86400000);
}

const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

// ── BOL deposit provenance (sql/092) ─────────────────────────────────────
// A deposit on an out-of-state BOL is either still REQUESTED from the
// client or COLLECTED by someone (initials + how). Same items/helpers as
// ClientPricingTab so the two cards read identically.
const BOL_DEPOSIT_STATUS_ITEMS = [
    { value: 'requested', label: 'Requested', color: '#d97706' },
    { value: 'collected', label: 'Collected', color: '#059669' },
];
const BOL_DEPOSIT_METHOD_ITEMS = [
    { value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'check', label: 'Check' },
    { value: 'zelle', label: 'Zelle' }, { value: 'other', label: 'Other' },
];
// Logged-in user's initials — display name from auth metadata when set,
// else the email local-part ("jaylen" → "JA", "jaylen.beaty" → "JB").
function userInitials(user) {
    const meta = user?.user_metadata || {};
    const name = meta.full_name || meta.display_name || meta.name || (user?.email || '').split('@')[0] || '';
    const parts = String(name).trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '';
    const init = parts.length === 1 ? parts[0].slice(0, 2) : parts.map(p => p[0]).join('').slice(0, 3);
    return init.toUpperCase();
}
// " · requested" / " · collected by JB (card)" — appended to deposit lines.
function bolDepositSuffix(e) {
    if (!(Number(e?.bol_deposit) > 0)) return '';
    if (e.bol_deposit_status === 'collected') {
        return ` · collected${e.bol_deposit_collected_by ? ` by ${e.bol_deposit_collected_by}` : ''}${e.bol_deposit_method ? ` (${e.bol_deposit_method})` : ''}`;
    }
    return ' · requested';
}

// Furniture subtitles — informational descriptions of each tier. Kept
// here (not in app_config) because they're content, not configurable colors.
const FURNITURE_SUBTITLES = {
    good:    'Entry-level / budget — composite wood, plastic, PVC tubes. Designed for quick assembly.',
    better:  'Mid-range / value — improved durability, better upholstery, varied finishes.',
    best:    'High-end / heirloom — solid wood, high-quality fabric, attention to detail.',
    luxury:  'Bespoke / prestige — custom-made with premium materials and superior craftsmanship.',
};

// Format a timestamp as "Xh ago" / "Xd ago" — small helper for the
// quote-given indicator.
function timeAgo(ts) {
    if (!ts) return '';
    const ms = Date.now() - new Date(ts).getTime();
    const h = Math.floor(ms / (1000 * 60 * 60));
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function EstimateExpandedDetails({ est, saveEstimate, showToast, showAlternatePricing = true }) {
    const [editingMove, setEditingMove] = useState(false);
    const [estForm, setEstForm] = useState({ ...est });
    const [saving, setSaving] = useState(false);
    const setEstField = (k, v) => setEstForm(f => ({ ...f, [k]: v }));

    // BOL (out-of-state) fields — often filled LATER than creation, once the
    // tariff comes back, even though crew/truck preallocation is already set.
    // Editable inline here with its own save; recomputes the estimate total.
    const [bolForm, setBolForm] = useState({
        bol_price: est.bol_price || '',
        estimated_weight_lbs: est.estimated_weight_lbs || '',
        bol_reference: est.bol_reference || '',
        bol_deposit: est.bol_deposit || '',
        // Provenance (sql/092) — status defaults to 'requested' once an
        // amount exists; the collected_* trio only matters when collected.
        bol_deposit_status: est.bol_deposit_status || null,
        bol_deposit_collected_by: est.bol_deposit_collected_by || '',
        bol_deposit_method: est.bol_deposit_method || null,
        bol_deposit_collected_at: est.bol_deposit_collected_at || null,
    });
    const [bolDirty, setBolDirty] = useState(false);
    const [bolSaving, setBolSaving] = useState(false);
    const setBolField = (k, v) => { setBolForm(f => ({ ...f, [k]: v })); setBolDirty(true); };
    const bolDepositAmt = parseFloat(bolForm.bol_deposit) || 0;
    const bolDepositStatus = bolForm.bol_deposit_status || 'requested';

    const { clients, jobs, estimates, moves, saveClient, saveMove, shiftMoveDates, confirmMoveDates, config, getCrewNoteSets, saveCrewNoteSet, loadAll } = useStore();
    const { navigateToClient } = useNav();
    // The move this option belongs to (sql/094). Every date / address edit
    // below goes to this row; the estimate's mirror columns follow.
    const move = moveForEstimate(est, moves);
    const siblingOptions = optionsForMove(estimates, est.move_id);
    const bookedSibling = siblingOptions.find(o => o.id !== est.id && o.status === 'booked') || null;
    // "Booked but not on the board" one-click push (Jaylen 2026-08-05:
    // the Phase Planner's Push to Dispatch button wasn't discoverable —
    // he booked an estimate and nothing appeared on dispatch, with no
    // signal why). When a BOOKED estimate has zero linked move_jobs, an
    // amber banner surfaces the push right here in the expanded row.
    const [pushingDispatch, setPushingDispatch] = useState(false);
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();

    // ── Move-date editing (with optional dispatch-job shift) ──────────────
    // The trip strip on the MoveHeaderCard is the date control (2026-09-18).
    // A load-date change on a booked move (or one with legs on the board)
    // parks here until the three-way confirm answers "shift the jobs too?".
    const [datePending, setDatePending] = useState(null); // { newDate, patch } awaiting the booked-jobs confirm
    const [dateSaving, setDateSaving] = useState(false);
    const linkedJobs = useMemo(
        () => (jobs || []).filter(j => j.estimate_id === est.id && !j._isSpanDay),
        [jobs, est.id],
    );
    // Legs on the dispatch board for the whole MOVE (any option) — the
    // date-shift confirm counts these, not just this option's jobs.
    const moveLegs = useMemo(
        () => (move ? legsForMove(jobs, move.id).filter(j => !j._isSpanDay) : []),
        [jobs, move],
    );

    // ── Crew note set (reusable crew-facing notes for this client) ────────
    const [crewSets, setCrewSets] = useState([]);
    const [crewSetId, setCrewSetId] = useState(est.crew_note_set_id || '');
    const [crewLabel, setCrewLabel] = useState('');
    const [crewBody, setCrewBody] = useState('');
    const [crewSaving, setCrewSaving] = useState(false);
    const [crewDirty, setCrewDirty] = useState(false);

    useEffect(() => {
        let alive = true;
        if (!est.client_id) return;
        getCrewNoteSets(est.client_id).then(sets => {
            if (!alive) return;
            setCrewSets(sets || []);
            // Show the explicitly-linked set if any; otherwise default to the
            // client's most recent set (sets are ordered newest-first). This
            // mirrors the crew portal's fallback so the office sees the same
            // note the crew will — even before it's formally selected here.
            const linked = (sets || []).find(s => s.id === (est.crew_note_set_id || ''));
            const active = linked || (sets && sets[0]) || null;
            if (active) { setCrewSetId(active.id); setCrewLabel(active.label || ''); setCrewBody(active.body || ''); }
        }).catch(() => {});
        return () => { alive = false; };
    }, [est.client_id, est.crew_note_set_id, getCrewNoteSets]);

    // Warn before leaving (tab close / refresh / navigate away) if crew notes
    // were edited but not saved — they have their own Save, separate from the
    // estimate, so it's easy to lose unsaved text.
    useEffect(() => {
        if (!crewDirty) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [crewDirty]);

    // Capability-based gates. Priority picker uses edit_clients (priority
    // is a client-level field). Pricing breakdown uses view_estimate_pricing.
    const { can, user, displayName } = useAuth();
    // Flip the BOL deposit to collected: stamp the time the FIRST time it
    // flips and prefill the collector with whoever is logged in. Saved by
    // the same Save BOL button as the amount.
    const markBolCollected = () => {
        setBolForm(f => ({
            ...f,
            bol_deposit_status: 'collected',
            bol_deposit_collected_at: f.bol_deposit_collected_at || new Date().toISOString(),
            bol_deposit_collected_by: f.bol_deposit_collected_by || userInitials(user),
        }));
        setBolDirty(true);
    };
    const canEditClient   = can('edit_clients');
    const canViewPricing  = can('view_estimate_pricing');
    const client = (clients || []).find(c => c.id === est.client_id) || null;

    // Priority + furniture class options resolved from app_config so
    // Settings → StatusManager edits propagate here without a code change.
    const PRIORITY_OPTIONS = useMemo(() => getPriorityOptions(config), [config?.priority_options]);
    const FURNITURE_CLASS_OPTIONS = useMemo(() => getFurnitureClassOptions(config), [config?.furniture_class_options]);

    const isLocked = est.status === 'completed' || (est._job_completed);
    const canEdit = !isLocked;

    // Lead-pipeline (sql/047) actions
    const currentPriority = est.priority || 'medium';
    const priorityOpt = PRIORITY_OPTIONS.find(p => p.key === currentPriority) || PRIORITY_OPTIONS[1] || { color: '#3b82f6', label: 'Medium' };
    const currentFurniture = client?.furniture_class || 'good';
    const furnitureOpt = FURNITURE_CLASS_OPTIONS.find(f => f.key === currentFurniture) || FURNITURE_CLASS_OPTIONS[0] || { color: '#a8a29e', label: 'Good' };

    // Priority and Furniture Class are set at estimate creation time in
    // ClientPricingTab — display-only here per Jaylen's 2026-05-14 call.
    // If a brother needs to change priority after creation, the full
    // EstimateModal (Edit button) is the right surface for that.

    // Single click moves the estimate out of the "ready to call out" queue
    // and into the "waiting to close" queue. Auto-promotes status from
    // any pre-quoted stage (building / ready) → 'quoted' if the sales
    // person hadn't already done that. Records method so the legal
    // verbal/written distinction is preserved.
    const markQuoteGiven = async (method) => {
        try {
            const patch = {
                ...est,
                quote_given_at: new Date().toISOString(),
                quote_method: method,
            };
            if (est.status === 'building' || est.status === 'ready') patch.status = 'quoted';
            await saveEstimate(patch);
            showToast(`${method === 'verbal' ? 'Verbal' : 'Written'} quote logged`);
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const undoQuoteGiven = async () => {
        if (!(await confirmDialog({ title: `Clear the quote-given stamp for ${client?.name || 'this client'}?`, body: `The quote date and method are cleared and ${client?.name || 'the estimate'} moves back to the callback queue. The estimate, its pricing and status are kept.`, confirmLabel: 'Clear stamp', danger: true }))) return;
        try {
            await saveEstimate({ ...est, quote_given_at: null, quote_method: null });
            showToast('Quote-given stamp cleared');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const toggleDraft = async () => {
        try {
            await saveEstimate({ ...est, is_draft: !est.is_draft });
            showToast(est.is_draft ? 'Draft flag cleared — back in callback queue' : 'Marked as draft — removed from callback queue');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const isOOS = est.job_type === 'LONG_DISTANCE';
    const hasRange = est.min_hours && est.max_hours && est.min_hours !== est.max_hours;
    const packingMinHrs = est.packing_min_hours || est.packing_hours || 0;
    const packingMaxHrs = est.packing_max_hours || est.packing_hours || 0;
    // Multi-day packing (2026-08-25): when more than one pack phase carries
    // its own hours + rate, sum those instead of hours-sum × the day-1
    // rate — different days can pack with different crews at different rates.
    // 2026-09-10: read through phasePackPricing — a pack day merged into a
    // move day (pack+move combo) keeps its packing pricing in the pack_*
    // slots and still counts here.
    const pricedPackPhases = (Array.isArray(est.planned_phases) ? est.planned_phases : []).filter(phaseHasPackPricing);
    const packPhasePriced = pricedPackPhases.length > 1;
    const packingMinTotal = packPhasePriced
        ? pricedPackPhases.reduce((s, p) => { const k = phasePackPricing(p); return s + k.min * k.rate; }, 0)
        : packingMinHrs * (est.packing_rate || 0);
    const packingMaxTotal = packPhasePriced
        ? pricedPackPhases.reduce((s, p) => { const k = phasePackPricing(p); return s + k.max * k.rate; }, 0)
        : packingMaxHrs * (est.packing_rate || 0);
    const hasPackingRange = packingMinHrs && packingMaxHrs && packingMinHrs !== packingMaxHrs;
    const heavyTotal = (est.heavy_item_count || 0) * (est.heavy_item_fee || 0);

    // Dirty tracking for the Move Details editor (2026-07-09, Jaylen):
    // the only Save was a tiny button at the TOP of the section — after
    // scrolling through the address editor it was invisible, and Cancel /
    // collapse discarded silently. Baseline snapshots on edit-entry.
    const moveEditBaselineRef = React.useRef(null);
    const startEditingMove = () => {
        // Re-seed from the live row (2026-09-18): the trip strip / other
        // surfaces may have changed the move (and so this estimate's mirror
        // columns) since this row was expanded — never overwrite the move's
        // addresses with a stale local copy.
        const fresh = { ...est };
        setEstForm(fresh);
        moveEditBaselineRef.current = JSON.stringify(fresh);
        setEditingMove(true);
    };
    const moveEditDirty = editingMove && moveEditBaselineRef.current !== null
        && JSON.stringify(estForm) !== moveEditBaselineRef.current;
    const cancelEditingMove = async () => {
        if (moveEditDirty) {
            const ok = await confirmDialog({
                title: `Discard changes to ${client?.name || 'this client'}'s move details?`,
                body: `Your unsaved edits are thrown away and ${client?.name || 'the estimate'} goes back to what was last saved.`,
                confirmLabel: 'Discard changes',
                cancelLabel: 'Keep editing',
                danger: true,
            });
            if (!ok) return;
        }
        setEstForm({ ...est });
        setEditingMove(false);
    };

    const handleSaveMoveDetails = async () => {
        setSaving(true);
        try {
            // Identity (addresses / stops / access / job type / packing /
            // heavy items / description) lives on the MOVE (rule 1). The db
            // strips those mirror columns from an estimate UPDATE, so they
            // must go through saveMove; saveEstimate still carries the
            // estimate-only fields (transfer truck, crew, pricing…).
            if (move) {
                const shaped = moveFieldsFromClientShape(estForm);
                const patch = {};
                MOVE_IDENTITY_FIELDS.forEach(k => { if (shaped[k] !== undefined) patch[k] = shaped[k]; });
                // Packing removed → the pack day goes with it (the packing
                // ChoiceKeys below already nulled it in the form).
                if (estForm.pack_date === null && move.pack_date) { patch.pack_date = null; patch.pack_time = null; patch.pack_tentative = false; }
                if (Object.keys(patch).length) await saveMove({ id: move.id, ...patch });
            }
            await saveEstimate(estForm);
            showToast('Estimate updated');
            setEditingMove(false);
        } catch(e) { showToast('Error: ' + e.message, 'error'); }
        finally { setSaving(false); }
    };

    // Save BOL fields + recompute the OOS total (mirrors ClientPricingTab:
    // total = bol_price + packing + heavy + removal − deposit).
    const handleSaveBol = async () => {
        // Negative money is never valid here — a typed "-500" used to save
        // and silently collapse a quoted estimate's totals to $0 (QA M2
        // BUG-6). The min="0" on the inputs only gates the spinner arrows.
        const negatives = ['bol_price', 'bol_deposit', 'estimated_weight_lbs']
            .filter(k => (parseFloat(bolForm[k]) || 0) < 0);
        if (negatives.length) {
            showToast('BOL values can\'t be negative — check the highlighted fields.', 'error');
            return;
        }
        setBolSaving(true);
        try {
            const bol = parseFloat(bolForm.bol_price) || 0;
            const deposit = parseFloat(bolForm.bol_deposit) || 0;
            const weight = bolForm.estimated_weight_lbs === '' ? null : (parseFloat(bolForm.estimated_weight_lbs) || null);
            // Same multi-day packing awareness as the display math above —
            // per-phase priced pack days sum at their OWN rates.
            const packingMin = packPhasePriced
                ? packingMinTotal
                : (est.packing_min_hours || est.packing_hours || 0) * (est.packing_rate || 0);
            const packingMax = packPhasePriced
                ? packingMaxTotal
                : (est.packing_max_hours || est.packing_hours || 0) * (est.packing_rate || 0);
            const heavy = (est.heavy_item_count || 0) * (est.heavy_item_fee || 0);
            const removal = est.removal_fee || 0;
            const base = bol + heavy + removal - deposit;
            const total_min = Math.max(0, base + packingMin);
            const total_max = Math.max(0, base + packingMax);
            // Provenance rides with the amount: no amount → no status; a
            // status other than collected clears the collector trio.
            const depositStatus = deposit > 0 ? (bolForm.bol_deposit_status || 'requested') : null;
            const collected = depositStatus === 'collected';
            await saveEstimate({
                ...est,
                bol_price: bol, bol_deposit: deposit, estimated_weight_lbs: weight,
                bol_reference: bolForm.bol_reference || '',
                bol_deposit_status: depositStatus,
                bol_deposit_collected_by: collected ? (String(bolForm.bol_deposit_collected_by || '').trim().toUpperCase().slice(0, 12) || null) : null,
                bol_deposit_method: collected ? (bolForm.bol_deposit_method || null) : null,
                bol_deposit_collected_at: collected ? (bolForm.bol_deposit_collected_at || new Date().toISOString()) : null,
                total_estimate: total_max, total_min, total_max,
            });
            setBolDirty(false);
            showToast('BOL updated');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setBolSaving(false); }
    };

    // ── Move-date handlers (2026-09-18: the trip strip writes the move) ───
    // Shift the load date through shiftMoveDates: `shiftJobs` moves the
    // board legs by the same delta and (wholeTrip) drags pack / unload
    // along; otherwise only the load date changes. Any other keys that
    // rode in the same patch are saved afterwards.
    const commitDateChange = async (shiftJobs) => {
        if (!datePending || !move) return;
        setDateSaving(true);
        try {
            const { load_date: _ld, ...rest } = datePending.patch || {};
            const res = await shiftMoveDates(move.id, datePending.newDate, { shiftJobs, wholeTrip: shiftJobs });
            if (Object.keys(rest).length) await saveMove({ id: move.id, ...rest });
            const n = (res?.shiftedJobs || []).length;
            showToast(shiftJobs
                ? (n > 0 ? `Dates changed — ${n} dispatch job${n === 1 ? '' : 's'} shifted` : 'Move dates shifted')
                : 'Load date updated (dispatch unchanged)');
            setDatePending(null);
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setDateSaving(false); }
    };
    // MoveHeaderCard / TripStrip onChange — receives ONLY the changed keys.
    // A load-date change on a booked move (or one with legs on the board)
    // asks how to handle the jobs; everything else saves straight away.
    const handleMoveChange = async (patch) => {
        if (!move || !patch || !Object.keys(patch).length) return;
        const changesLoad = Object.prototype.hasOwnProperty.call(patch, 'load_date')
            && patch.load_date && move.load_date && patch.load_date !== move.load_date;
        const isBooked = move.status === 'booked' || est.status === 'booked';
        if (changesLoad && (isBooked || moveLegs.length > 0)) {
            setDatePending({ newDate: patch.load_date, patch });
            return;
        }
        setDateSaving(true);
        try {
            await saveMove({ id: move.id, ...patch });
            showToast('Move dates updated');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setDateSaving(false); }
    };
    // "Alternate pricing" (rule 2) — a new option on the SAME move. The
    // builder lives on the client file's Estimates tab; navigateToClient
    // only takes an id, so we send them there and say where to click.
    const goAlternatePricing = () => {
        if (!est.client_id) return;
        navigateToClient(est.client_id);
        showToast('Use Alternate pricing on the Estimates tab');
    };

    // ── Crew-notes handlers ───────────────────────────────────────────────
    const selectCrewSet = (id) => {
        if (id === '__new__') { setCrewSetId(''); setCrewLabel(''); setCrewBody(''); setCrewDirty(true); return; }
        const s = crewSets.find(x => x.id === id);
        setCrewSetId(id); setCrewLabel(s?.label || ''); setCrewBody(s?.body || ''); setCrewDirty(true);
    };
    const handleSaveCrewNotes = async () => {
        setCrewSaving(true);
        try {
            const fallbackLabel = `Crew notes · ${(move ? fmtMoveDate(move.load_date, { tentative: move.load_tentative, year: true }) : fmtDate(est.move_date)) || format(new Date(), 'MMM yyyy')}${est.job_number ? ' · ' + est.job_number : ''}`;
            const saved = await saveCrewNoteSet({
                id: crewSetId || undefined,
                client_id: est.client_id,
                label: (crewLabel && crewLabel.trim()) || fallbackLabel,
                body: crewBody,
            });
            if (est.crew_note_set_id !== saved.id) {
                await saveEstimate({ ...est, crew_note_set_id: saved.id });
            }
            const sets = await getCrewNoteSets(est.client_id);
            setCrewSets(sets || []);
            setCrewSetId(saved.id); setCrewLabel(saved.label || ''); setCrewDirty(false);
            showToast('Crew notes saved');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setCrewSaving(false); }
    };

    // Route status changes through the shared safeguard hook so the
    // inline status picker on the Estimates page row gets the same
    // confirmation dialog as the full EstimateModal. Without this, the
    // BOOKED→Quoted→BOOKED bug-prone flip would skip the warning here
    // even though it has the same operational consequences.
    const statusChanger = useEstimateStatusChange({
        currentStatus: est?.status,
        isLocked,
        // Book confirm carries the move's date chips (2026-09-18, rule 4):
        // the ✓ chips are logged via confirmMoveDates with the booker's
        // name; a tentative date never blocks the booking.
        move,
        confirmDates: keys => confirmMoveDates(move.id, keys, displayName),
        commit: async (newStatus) => {
            await saveEstimate({ ...est, status: newStatus });
            showToast(`Status updated to ${ESTIMATE_STATUSES.find(s => s.key === newStatus)?.label || newStatus}`);
        },
    });

    return (
        <div className="border-t border-slate-100 px-4 py-4 bg-slate-50/50 space-y-4">
            {/* Estimate Status picker removed from this view 2026-05-14 —
                status is now editable from the StatusDropdown on the
                collapsed row. statusChanger is preserved for the safeguard
                dialog only (renders when row's StatusDropdown commits a
                risky transition). */}
            {isLocked && (
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-red-500 font-bold">&#x1F512; Locked (job completed)</span>
                </div>
            )}
            {statusChanger.dialog}

            {/* ===== EDITOR HEADER (2026-09-18, moves-architecture.md §3–4) =====
                Option label ("021-A" when the move has several options), the
                Declined pill when the client went with a sibling, and the
                "Alternate pricing" action (rule 2: a new option on the same
                move — built on the client file's Estimates tab). Below it the
                MoveHeaderCard: trip strip (the ONLY date control), route, job
                type and move status, with one Edit for addresses / access. */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="font-mono text-xs text-slate-400 font-bold whitespace-nowrap" title={est.job_number || ''}>{optionLabel(est, siblingOptions.length)}</span>
                    {siblingOptions.length > 1 && (
                        <span className="text-[10px] text-slate-400">{siblingOptions.length} options on this move</span>
                    )}
                    {est.status === 'declined' && (() => {
                        const s = ESTIMATE_STATUSES.find(x => x.key === 'declined');
                        return (
                            <>
                                <StatusPill label={s?.label || 'Declined'} color={s?.color || '#94a3b8'} bg={s?.bg} />
                                {bookedSibling && (
                                    <span className="text-[11px] text-slate-500">Client picked {bookedSibling.option_letter || optionLabel(bookedSibling, siblingOptions.length)} instead</span>
                                )}
                            </>
                        );
                    })()}
                </div>
                {/* Alternate pricing moved to the row / tab strip on the Estimates
                    page (2026-09-19, estimates-page.md 2A); the EstimateModal keeps
                    this button since it has no tab strip. */}
                {est.client_id && !isLocked && showAlternatePricing && (
                    <Button variant="secondary" size="sm" onClick={goAlternatePricing} title="Price this same move another way — a new option with the same dates and addresses">
                        Alternate pricing
                    </Button>
                )}
            </div>
            {move ? (
                <MoveHeaderCard
                    move={move}
                    compact
                    disabled={!canEdit || dateSaving}
                    onChange={handleMoveChange}
                    onEdit={editingMove ? undefined : startEditingMove}
                    extra={est.status === 'booked' && moveLegs.length > 0 ? (
                        <span className="text-[10px] text-slate-400">{moveLegs.length} job{moveLegs.length === 1 ? '' : 's'} on the board — a load-date change asks whether to shift {moveLegs.length === 1 ? 'it' : 'them'}</span>
                    ) : null}
                />
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Move Date</div>
                    <div className="text-sm font-semibold text-slate-800 mt-0.5">{fmtMoveDate(est.move_date, { year: true }) || <span className="text-slate-400 font-normal">No date set</span>}</div>
                    <div className="text-[10px] text-slate-400 mt-1">This estimate isn't linked to a move yet — dates and addresses are edited on the client file.</div>
                </div>
            )}

            {/* ===== LEAD PIPELINE (sql/047) =====
                Priority + furniture class + quote-given action live here.
                Drives the dashboard's callback / waiting-to-close queues.
                Office staff / brothers update from this panel without
                opening the full EstimateModal. */}
            {!isLocked && (
                <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-3">
                    {/* Priority + Furniture Class display.
                          • Priority — uses the shared PriorityPicker chip
                            variant. Admin/owner can click to change (with
                            confirmation when changing an existing value);
                            others see a display-only chip. Writes to
                            clients.priority (sql/049).
                          • Furniture — locked from edit here because the
                            estimate already exists (sql/048 + 2026-05-14
                            convention: set once during estimate creation,
                            never again). Display-only chip + subtitle. */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Priority:</span>
                            <PriorityPicker
                                client={client}
                                editable={canEditClient && !isLocked}
                                variant="chip"
                                onChanged={() => showToast(`${client?.name || 'Client'} priority updated`)}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Furniture:</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white"
                                style={{ backgroundColor: furnitureOpt.color }}
                                title="Locked — set at estimate creation, not editable after">
                                {furnitureOpt.label} 🔒
                            </span>
                            <span className="text-[10px] text-slate-500 italic">{FURNITURE_SUBTITLES[currentFurniture]}</span>
                        </div>
                    </div>

                    {/* Quote-given action — boundary between "ready to call
                        out" and "waiting to close" queues on the dashboard. */}
                    {!est.quote_given_at ? (
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-bold text-amber-700">📞 Quote not yet given to client</div>
                                <div className="text-[10px] text-slate-500">In office callback queue. Mark when sales reaches the client.</div>
                            </div>
                            <Button variant="success" size="sm" onClick={() => markQuoteGiven('verbal')}>
                                Verbal Quote Given
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => markQuoteGiven('written')}>
                                Written
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-bold text-emerald-700">
                                    ✅ {est.quote_method === 'written' ? 'Written' : 'Verbal'} quote given · {timeAgo(est.quote_given_at)}
                                </div>
                                <div className="text-[10px] text-slate-500">Waiting on client to close. Move to Booked when they say yes.</div>
                            </div>
                            <Button variant="tertiary" size="sm" onClick={undoQuoteGiven}>
                                Undo
                            </Button>
                        </div>
                    )}

                    {/* Draft toggle — rare opt-out from the callback queue */}
                    <div className="pt-2 border-t border-slate-100">
                        <Switch size="sm" checked={!!est.is_draft} onChange={() => toggleDraft()} label="Save as draft (hide from callback queue)" />
                    </div>
                </div>
            )}

            {/* ===== PRICING BREAKDOWN ===== */}
            {canViewPricing && (
                <div className="space-y-2">
                    {/* BOL price */}
                    {isOOS && est.bol_price > 0 && (
                        <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <span className="text-xs font-bold text-blue-700">BOL Move Price (tariff)</span>
                            <span className="text-sm font-bold text-blue-800">${Number(est.bol_price).toLocaleString()}</span>
                        </div>
                    )}
                    {est.bol_reference && (
                        <div className="text-xs text-blue-500 px-3">BOL Ref: {est.bol_reference}</div>
                    )}
                    {/* BOL deposit — pre-paid at sign-time, shown as a credit */}
                    {isOOS && (est.bol_deposit || 0) > 0 && (
                        <div className="flex justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <span className="text-xs font-bold text-emerald-700">BOL Deposit<span className="font-semibold text-emerald-600">{bolDepositSuffix(est)}</span></span>
                            <span className="text-sm font-bold text-emerald-800">- ${Number(est.bol_deposit).toLocaleString()}</span>
                        </div>
                    )}

                    {/* Labor (local) — priced at the EFFECTIVE rate (base +
                        extra-truck surcharge), matching how the stored total
                        was computed. Showing the raw base rate here made the
                        line items sum short of the total (QA M2 BUG-3). */}
                    {!isOOS && (() => {
                        const effRate = estimateEffectiveRate(est, config);
                        const surcharge = estimateExtraTruckSurcharge(est, config);
                        return (
                        <div className="flex justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <div>
                                <span className="text-xs font-bold text-slate-600">Labor</span>
                                <span className="text-[10px] text-slate-400 ml-2">
                                    {hasRange ? `${est.min_hours}–${est.max_hours} hrs` : `${est.estimated_hours || 0} hrs`} × ${effRate}/hr
                                    {surcharge > 0 && ` ($${est.hourly_rate || 0} + $${surcharge} extra trucks)`}
                                </span>
                            </div>
                            <span className="text-sm font-bold text-slate-700">
                                {hasRange
                                    ? `$${(est.min_hours * effRate).toLocaleString()} – $${(est.max_hours * effRate).toLocaleString()}`
                                    : `$${((est.estimated_hours || 0) * effRate).toLocaleString()}`
                                }
                            </span>
                        </div>
                        );
                    })()}

                    {/* Packing */}
                    {packingMinTotal > 0 && (
                        <div className="flex justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div>
                                <span className="text-xs font-bold text-amber-700">Packing{packPhasePriced ? ` — ${pricedPackPhases.length} days` : ''}</span>
                                {packPhasePriced ? (
                                    <span className="text-[10px] text-amber-500 ml-2">
                                        {pricedPackPhases.map((p, i) => {
                                            const k = phasePackPricing(p);
                                            return `Day ${i + 1}: ${p.crew_size || est.packing_crew_size} packers · ${k.min}${k.max !== k.min ? `–${k.max}` : ''}h × $${k.rate}/hr`;
                                        }).join(' · ')}
                                    </span>
                                ) : (
                                    <span className="text-[10px] text-amber-500 ml-2">
                                        {est.packing_crew_size} packers × {packingMinHrs}{hasPackingRange ? `–${packingMaxHrs}` : ''} hrs × ${est.packing_rate}/hr
                                    </span>
                                )}
                            </div>
                            <span className="text-sm font-bold text-amber-800">
                                {packingMinTotal !== packingMaxTotal && packingMaxTotal > 0
                                    ? `$${packingMinTotal.toLocaleString()} – $${packingMaxTotal.toLocaleString()}`
                                    : `$${packingMinTotal.toLocaleString()}`
                                }
                            </span>
                        </div>
                    )}

                    {/* Other fees */}
                    <div className="grid grid-cols-2 gap-2">
                        {/* Travel/materials are LOCAL-only fees — the OOS total
                            never includes them, so showing them on OOS rows made
                            line items sum past the total (QA M2 BUG-4). */}
                        {!isOOS && (est.travel_fee || 0) > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                                <div className="text-[10px] text-slate-400 font-bold uppercase">Travel</div>
                                <div className="text-sm font-bold text-slate-700">${Number(est.travel_fee).toLocaleString()}</div>
                            </div>
                        )}
                        {!isOOS && (est.hold_fee || 0) > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                                <div className="text-[10px] text-slate-400 font-bold uppercase">Hold</div>
                                <div className="text-sm font-bold text-slate-700">${Number(est.hold_fee).toLocaleString()}</div>
                            </div>
                        )}
                        {!isOOS && (est.materials_fee || 0) > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                                <div className="text-[10px] text-slate-400 font-bold uppercase">Materials</div>
                                <div className="text-sm font-bold text-slate-700">${Number(est.materials_fee).toLocaleString()}</div>
                            </div>
                        )}
                        {heavyTotal > 0 && (
                            <div className="bg-rose-50 rounded-lg p-3 border border-rose-200 text-center">
                                <div className="text-[10px] text-rose-400 font-bold uppercase">Heavy Items ({est.heavy_item_count})</div>
                                <div className="text-sm font-bold text-rose-700">${heavyTotal.toLocaleString()}</div>
                            </div>
                        )}
                        {(est.removal_fee || 0) > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                                <div className="text-[10px] text-slate-400 font-bold uppercase">Removal</div>
                                <div className="text-sm font-bold text-slate-700">${Number(est.removal_fee).toLocaleString()}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== BILL OF LADING — editable, often filled after creation ===== */}
            {isOOS && canEdit && can('edit_pricing') && (
                <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">Bill of Lading</span>
                        {bolDirty && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide">Unsaved</span>}
                    </div>
                    <p className="text-[10px] text-slate-500">Add these once the tariff comes back — crew/truck preallocation is already saved. Saving updates the estimate total.</p>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Est. Weight (lbs)
                            <NumberField step={100} suffix="lbs" className="mt-0.5" value={bolForm.estimated_weight_lbs} onChange={v => setBolField('estimated_weight_lbs', v)} placeholder="6500" ariaLabel="Estimated weight (lbs)" />
                        </label>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">BOL Move Price ($)
                            <NumberField step={50} prefix="$" className="mt-0.5" inputClassName="font-semibold" value={bolForm.bol_price} onChange={v => setBolField('bol_price', v)} placeholder="Tariff total" ariaLabel="BOL move price" />
                        </label>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">BOL / Reference #
                            <input className={`${inputCls} mt-0.5`} value={bolForm.bol_reference} onChange={e => setBolField('bol_reference', e.target.value)} placeholder="BOL number" />
                        </label>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Deposit ($)
                            <NumberField step={50} prefix="$" className="mt-0.5" value={bolForm.bol_deposit} onChange={v => setBolField('bol_deposit', v)} placeholder="0" ariaLabel="BOL deposit" />
                        </label>
                        {/* Deposit provenance (sql/092): requested vs collected, and
                            by whom / how. Only meaningful once there's an amount. */}
                        {bolDepositAmt > 0 && (
                            <div className="col-span-2 space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <ChoiceKeys size="sm" ariaLabel="Deposit status" items={BOL_DEPOSIT_STATUS_ITEMS} value={bolDepositStatus}
                                        onChange={v => { if (v === 'collected') markBolCollected(); else setBolField('bol_deposit_status', v); }} />
                                    {bolDepositStatus === 'requested' && (
                                        <Button variant="secondary" size="sm" onClick={markBolCollected}>Mark collected</Button>
                                    )}
                                </div>
                                {bolDepositStatus === 'collected' && (
                                    <div className="grid grid-cols-2 gap-2">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Collected by (initials)
                                            <input className={`${inputCls} mt-0.5 uppercase`} maxLength={12} value={bolForm.bol_deposit_collected_by || ''}
                                                onChange={e => setBolField('bol_deposit_collected_by', e.target.value)}
                                                onBlur={e => setBolField('bol_deposit_collected_by', e.target.value.trim().toUpperCase())}
                                                placeholder="JB" />
                                        </label>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Method
                                            <ChoiceKeys size="sm" className="mt-0.5" ariaLabel="Deposit method" items={BOL_DEPOSIT_METHOD_ITEMS} value={bolForm.bol_deposit_method || ''} onChange={v => setBolField('bol_deposit_method', v)} />
                                        </div>
                                        {bolForm.bol_deposit_collected_at && (
                                            <div className="col-span-2 text-[10px] text-slate-400 normal-case">
                                                on {(() => { try { return format(parseISO(bolForm.bol_deposit_collected_at), 'MMM d, yyyy'); } catch { return bolForm.bol_deposit_collected_at; } })()}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(parseFloat(bolForm.bol_price) || 0) > 0 && (
                                    <div className="text-[11px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                        Tariff ${(parseFloat(bolForm.bol_price) || 0).toLocaleString()} − Deposit ${bolDepositAmt.toLocaleString()} = <span className="font-bold">${Math.max(0, (parseFloat(bolForm.bol_price) || 0) - bolDepositAmt).toLocaleString()}</span> outstanding
                                        <span className="font-medium text-emerald-600">{bolDepositSuffix({ bol_deposit: bolDepositAmt, bol_deposit_status: bolDepositStatus, bol_deposit_collected_by: bolForm.bol_deposit_collected_by, bol_deposit_method: bolForm.bol_deposit_method })}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleSaveBol} loading={bolSaving} disabled={!bolDirty}>
                            {bolDirty ? 'Save BOL' : 'Saved'}
                        </Button>
                    </div>
                </div>
            )}

            {/* (The stand-alone "Move Date / Change Date" block moved into the
                MoveHeaderCard's trip strip at the top of this editor, 2026-09-18.) */}

            {/* ===== BOOKED BUT NOT ON THE BOARD — one-click push ===== */}
            {est.status === 'booked' && linkedJobs.length === 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-sm font-bold text-amber-900">⚠ Booked, but not on the dispatch board</div>
                        <div className="text-xs text-amber-800 mt-0.5">
                            {(Array.isArray(est.planned_phases) && est.planned_phases.length > 0)
                                ? `${est.planned_phases.length} planned phase${est.planned_phases.length === 1 ? '' : 's'} ready to push — no jobs exist yet.`
                                : 'No phases are planned yet — open this estimate\'s Phase Planner first, then push.'}
                        </div>
                    </div>
                    <Button
                        loading={pushingDispatch}
                        disabled={!(Array.isArray(est.planned_phases) && est.planned_phases.length > 0)}
                        title={(Array.isArray(est.planned_phases) && est.planned_phases.length > 0) ? 'Create the dispatch-board jobs from the planned phases' : 'Add at least one phase in the Phase Planner first'}
                        onClick={async () => {
                            setPushingDispatch(true);
                            try {
                                const r = await db.pushPhasesToDispatch(est.id);
                                showToast(`Pushed to dispatch — ${r.created || 0} job${(r.created || 0) === 1 ? '' : 's'} created`);
                                await loadAll();
                            } catch (e) {
                                showToast('Error: ' + e.message, 'error');
                            } finally {
                                setPushingDispatch(false);
                            }
                        }}
                    >
                        Push to Dispatch
                    </Button>
                </div>
            )}

            {/* ===== CREW NOTES — reusable per-client, synced everywhere ===== */}
            {!isLocked && (
                <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Crew Notes</span>
                        {crewDirty && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide">Unsaved</span>}
                    </div>
                    {/* Selector — left-aligned under the header, app-styled. */}
                    {crewSets.length > 0 && (
                        <Combobox
                            size="sm"
                            className="max-w-md"
                            items={[
                                ...crewSets.map(s => ({
                                    value: s.id,
                                    label: s.label || 'Crew notes',
                                    sub: s.created_at ? new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : undefined,
                                })),
                                { value: '__new__', label: '+ New crew note set' },
                            ]}
                            value={crewSetId || ''}
                            onChange={v => { if (v !== '') selectCrewSet(v); }}
                            clearable={false}
                            placeholder="Select a note set…"
                            ariaLabel="Crew note set" />
                    )}
                    {(crewSetId || crewDirty || crewSets.length === 0) ? (
                        <>
                            <input className={`${inputCls} text-xs`} placeholder="Label (e.g. Garage cleanout · Jun 2026)" value={crewLabel} onChange={e => { setCrewLabel(e.target.value); setCrewDirty(true); }} />
                            <textarea className={`${inputCls} text-xs`} rows={3} placeholder="Notes for the crew — gate code, where to park the truck, fragile items, pets…" value={crewBody} onChange={e => { setCrewBody(e.target.value); setCrewDirty(true); }} />
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-[10px] text-slate-400">Shown to the crew on this job · edits sync everywhere this set is used.</span>
                                <Button onClick={handleSaveCrewNotes} loading={crewSaving} disabled={!crewDirty}>
                                    {crewDirty ? 'Save Crew Notes' : 'Saved'}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button variant="tertiary" size="sm" onClick={() => selectCrewSet('__new__')}>+ Add crew notes</Button>
                    )}
                </div>
            )}

            {editingMove ? (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-400 uppercase">
                            Editing Move Details
                            {moveEditDirty && <span className="ml-2 normal-case font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px]">Unsaved changes</span>}
                        </span>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveMoveDetails} loading={saving}>Save</Button>
                            <Button variant="secondary" size="sm" onClick={cancelEditingMove}>Cancel</Button>
                        </div>
                    </div>
                    <MoveDetailsEditor form={estForm} set={setEstField} setBatch={(u) => setEstForm(f => ({ ...f, ...u }))} showDistances={false} />
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Job Type">
                            <Combobox
                                items={JOB_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))}
                                value={estForm.job_type || 'LOCAL'}
                                onChange={v => { if (v !== '') setEstField('job_type', v); }}
                                clearable={false}
                                placeholder="Job type" />
                        </FormField>
                        <FormField label="Packing">
                            <ChoiceKeys items={PACKING_OPTIONS} value={estForm.packing_needed || 'tbd'} ariaLabel="Packing" onChange={async v => {
                                const next = v;
                                // Warn before clearing a set packing day.
                                if ((estForm.packing_needed === 'partial' || estForm.packing_needed === 'full')
                                    && next !== 'partial' && next !== 'full'
                                    && estForm.pack_date) {
                                    if (!(await confirmDialog({ title: `Remove packing from ${client?.name || 'this client'}'s estimate?`, body: `The packing day${estForm.pack_date ? ` (${fmtDate(estForm.pack_date)})` : ''} is cleared and the pack phase drops off the dispatch board on the next push. The move itself and the rest of the estimate are kept.`, confirmLabel: 'Remove packing', danger: true }))) return;
                                    setEstForm(f => ({ ...f, packing_needed: next, pack_date: null, pack_time: null }));
                                    return;
                                }
                                setEstField('packing_needed', next);
                            }} />
                        </FormField>
                    </div>
                    {/* Packing day — lives on the move's trip strip (the Pack
                        tile in the header card above) since 2026-09-18; rule 1:
                        one date control. This section only carries packing on/off. */}
                    {(estForm.packing_needed === 'partial' || estForm.packing_needed === 'full') && (
                        <p className="text-[11px] text-slate-400">
                            Pack day: {move?.pack_date
                                ? <span className="text-slate-700 font-semibold">{fmtMoveDate(move.pack_date, { tentative: move.pack_tentative, year: true })}{move.pack_time ? ` · ${move.pack_time}` : ''}</span>
                                : 'not set'} — pick or change it on the Pack tile in the move header above.
                        </p>
                    )}
                    <FormField label="Heavy / Special Items"><input className={inputCls} value={estForm.heavy_items || ''} onChange={e => setEstField('heavy_items', e.target.value)} placeholder="Piano, gun safe..." /></FormField>
                    {/* Transfer truck (sql/081, Jaylen 2026-08-10): a small
                        shuttle for a steep driveway / tight access — needed
                        on ONE side of the move, not the whole trip. Covered
                        legs get +1 truck in dispatch requirements, and a
                        truck assigned there stays on that leg (the board's
                        truck cascade skips it). */}
                    <FormField label="Transfer Truck (shuttle — steep driveway / tight access)">
                        <div className="flex gap-1.5 flex-wrap">
                            {[
                                { v: null, l: 'Off' },
                                { v: 'load', l: 'Load day' },
                                { v: 'unload', l: 'Unload day' },
                                { v: 'both', l: 'Both ends' },
                            ].map(o => {
                                const active = (estForm.transfer_truck || null) === o.v;
                                return (
                                    <button key={String(o.v)} type="button"
                                        onClick={() => setEstField('transfer_truck', o.v)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                                        {o.l}
                                    </button>
                                );
                            })}
                        </div>
                        {estForm.transfer_truck && (
                            <p className="text-[11px] text-slate-400 mt-1">Dispatch will require one extra (small) truck on the {estForm.transfer_truck === 'both' ? 'load and unload legs' : `${estForm.transfer_truck} leg`} — and it won't carry to the rest of the trip.</p>
                        )}
                    </FormField>
                    {/* Bottom save bar — the top buttons scroll out of view on
                        this long form; this is the one the user actually sees
                        when they finish editing (2026-07-09, Jaylen). */}
                    <div className="flex gap-2 pt-1">
                        <Button className="flex-1" onClick={handleSaveMoveDetails} loading={saving}>
                            {moveEditDirty ? 'Save Move Details' : '✓ Saved'}
                        </Button>
                        <Button variant="secondary" onClick={cancelEditingMove}>Cancel</Button>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-slate-400 uppercase">Move Details</span>
                        {canEdit && <Button variant="tertiary" size="sm" onClick={startEditingMove}>Edit Move Details</Button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div><span className="text-slate-400 font-bold">Job Type:</span> <span className="text-slate-700">{isOOS ? 'Out-of-State (BOL)' : est.job_type}</span></div>
                        <div><span className="text-slate-400 font-bold">Packing:</span> <span className="text-slate-700">{est.packing_needed}</span></div>
                        {/* Dates read from the move with the tentative marker
                            (rule 3, 2026-09-18); the mirror columns are the
                            fallback for an unlinked estimate. */}
                        {(move ? move.load_date : est.move_date) && (
                            <div><span className="text-slate-400 font-bold">{move?.unload_date ? 'Load:' : 'Move Date:'}</span>{' '}
                                <span className={`text-slate-700 ${move?.load_tentative ? 'text-amber-700' : ''}`}>{move ? fmtMoveDate(move.load_date, { tentative: move.load_tentative, year: true }) : fmtMoveDate(est.move_date, { year: true })}{(move ? move.load_time : est.move_time) ? ` · ${move ? move.load_time : est.move_time}` : ''}</span>
                            </div>
                        )}
                        {move?.unload_date && (
                            <div><span className="text-slate-400 font-bold">Unload:</span>{' '}
                                <span className={`text-slate-700 ${move.unload_tentative ? 'text-amber-700' : ''}`}>{fmtMoveDate(move.unload_date, { tentative: move.unload_tentative, windowEnd: move.unload_window_end, year: true })}{move.unload_time ? ` · ${move.unload_time}` : ''}</span>
                            </div>
                        )}
                        {(move ? move.pack_date : est.pack_date) && (
                            <div><span className="text-slate-400 font-bold">Packing Day:</span>{' '}
                                <span className={`text-slate-700 ${move?.pack_tentative ? 'text-amber-700' : ''}`}>{move ? fmtMoveDate(move.pack_date, { tentative: move.pack_tentative, year: true }) : fmtMoveDate(est.pack_date, { year: true })}{(move ? move.pack_time : est.pack_time) ? ` · ${move ? move.pack_time : est.pack_time}` : ''}</span>
                            </div>
                        )}
                        {est.crew_size_needed > 0 && (
                            <div>
                                <span className="text-slate-400 font-bold">{isOOS ? 'Load Crew:' : 'Crew:'}</span>{' '}
                                <span className="text-slate-700">{est.crew_size_needed} people</span>
                            </div>
                        )}
                        {isOOS && (est.travel_crew_size || 0) > 0 && (
                            <div>
                                <span className="text-slate-400 font-bold">Travel Crew:</span>{' '}
                                <span className="text-slate-700">{est.travel_crew_size} on trip</span>
                            </div>
                        )}
                        {estimateTruckLabel(est) && <div><span className="text-slate-400 font-bold">{estimateTruckLabel(est).includes('+') ? 'Trucks:' : 'Truck:'}</span> <span className="text-slate-700">{estimateTruckLabel(est)}</span></div>}
                        {est.transfer_truck && <div><span className="text-slate-400 font-bold">Transfer Truck:</span> <span className="text-slate-700">shuttle on {est.transfer_truck === 'both' ? 'both ends' : `${est.transfer_truck} day`}</span></div>}
                        {est.heavy_items && <div className="col-span-2"><span className="text-slate-400 font-bold">Heavy Items:</span> <span className="text-slate-700">{est.heavy_items}</span></div>}
                        {est.description && <div className="col-span-2"><span className="text-slate-400 font-bold">Notes:</span> <span className="text-slate-700">{est.description}</span></div>}
                        <div><span className="text-slate-400 font-bold">From:</span> <span className="text-slate-700">{est.origin_address || 'Not set'}</span></div>
                        <div><span className="text-slate-400 font-bold">To:</span> <span className="text-slate-700">{est.destination_address || 'Not set'}</span></div>
                    </div>
                </div>
            )}

            {/* Unified Notes (Jaylen 2026-07-02) — the SAME Office/Crew notes
                panel from the client file + estimator app, keyed to this
                estimate's client. Same rows, so edits here sync everywhere
                (and vice versa) automatically. */}
            {est.client_id && (
                <div className="mt-4">
                    <ClientNotesPanel clientId={est.client_id} />
                </div>
            )}

            {/* Booked-estimate date change — choose whether to shift the
                dispatch jobs by the same number of days or change only the
                estimate's date. */}
            <ConfirmDialog
                open={!!datePending}
                title={datePending ? `Move ${client?.name || 'this client'} to ${fmtDate(datePending.newDate)}?` : 'Change move date?'}
                confirmLabel={moveLegs.length > 0 ? `Shift ${moveLegs.length} job${moveLegs.length === 1 ? '' : 's'}` : 'Shift the whole trip'}
                secondaryLabel="Just the load date"
                cancelLabel="Cancel"
                onConfirm={() => commitDateChange(true)}
                onSecondary={() => commitDateChange(false)}
                onCancel={() => setDatePending(null)}>
                {datePending && move && (
                    <>
                        Move <span className="font-semibold text-slate-900">{client?.name || 'this client'}</span>'s load day from{' '}
                        <span className="font-bold text-slate-700">{fmtMoveDate(move.load_date, { tentative: move.load_tentative, year: true })}</span> to{' '}
                        <span className="font-bold text-emerald-700">{fmtDate(datePending.newDate)}</span>{' '}
                        ({Math.abs(dayDelta(move.load_date, datePending.newDate))} day{Math.abs(dayDelta(move.load_date, datePending.newDate)) === 1 ? '' : 's'}{' '}
                        {dayDelta(move.load_date, datePending.newDate) < 0 ? 'earlier' : 'later'}).
                        <div className="mt-2">
                            {moveLegs.length > 0 ? (
                                <>This move has <span className="font-semibold">{moveLegs.length}</span> scheduled job{moveLegs.length === 1 ? '' : 's'} on the dispatch board.
                                    Shift {moveLegs.length === 1 ? 'it' : 'them'} — and the pack / unload days — by the same amount, or change just the load date?</>
                            ) : (
                                <>This move is booked. Shift the pack / unload days by the same amount, or change just the load date?</>
                            )}
                        </div>
                    </>
                )}
            </ConfirmDialog>
            {confirmElement}
        </div>
    );
}
